import React from 'react';
import type { EventOverdueRow } from '../data/overdue';
import { overdueTooltip, urgentTooltip } from '../data/overdue';

// Inline SVG icons kept tiny (no external dependency)
const OverdueIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 4v4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <circle cx="8" cy="11" r="0.75" fill="currentColor" />
  </svg>
);

const UrgentIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M8 1.5L14.5 13.5H1.5L8 1.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    <path d="M8 6v3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <circle cx="8" cy="11.5" r="0.65" fill="currentColor" />
  </svg>
);

interface EventFlagsProps {
  row: EventOverdueRow | null | undefined;
  /** Compact mode: icon-only, no text label */
  compact?: boolean;
}

/**
 * Renders inline overdue/urgent icon badges for an event.
 * Designed to sit next to a stage pill without adding visual bulk.
 */
export default function EventFlags({ row, compact }: EventFlagsProps) {
  if (!row) return null;
  const showOverdue = row.is_overdue;
  const showUrgent = row.is_urgent;
  if (!showOverdue && !showUrgent) return null;

  return (
    <>
      {showOverdue && (
        <span
          className="event-flag event-flag--overdue"
          title={overdueTooltip(row)}
          aria-label={overdueTooltip(row)}
        >
          <OverdueIcon className="event-flag__icon" />
          {!compact && <span className="event-flag__label">Overdue</span>}
        </span>
      )}
      {showUrgent && (
        <span
          className="event-flag event-flag--urgent"
          title={urgentTooltip(row)}
          aria-label={urgentTooltip(row)}
        >
          <UrgentIcon className="event-flag__icon" />
          {!compact && <span className="event-flag__label">Urgent</span>}
        </span>
      )}
    </>
  );
}
